import { useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from 'react-query'

import {
    IconDollarCircleArrowsNoShadow,
    IconDotInCircle,
    IconDownChevron
} from 'assets/svg/sprite'
import { addToast } from 'components/Toast/Toast'
import AddTrialLink from 'components/UI/Modal/AddTrialLink/AddTrialLink'
import StackedAvatar from 'components/UI/StackedAvatar/StackedAvatar'
import { useModalContext } from 'context/modalContext'
import LayoutHeader from 'features/LayoutHeader/LayoutHeader'
import WithHeaderSection from 'layouts/WithHeaderSection/WithHeaderSection'
import {
    deletePromoCampaign,
    getFreeTrialLinks,
    updatePromoCampaign
} from 'services/endpoints/settings/general/subscription'
import { type PromoCampaign } from 'types/api/subscription'

import style from './_free-trial.module.scss'
import { ClaimedUsersModal } from '../components/ClaimedUsersModal'
import { SubscriptionCard } from '../components/SubscriptionCard'

export const FreeTrial = () => {
    const { t } = useTranslation()
    const queryClient = useQueryClient()
    const modalData = useModalContext()
    const dateArray = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec'
    ]
    const [activeFilter, setActiveFilter] = useState('Active')
    const [openTrialOptions, setOpenTrialOptions] = useState<
        Record<number, boolean>
    >({})
    const filterTabs = ['Active', 'Disabled', 'Expired']

    const toggleCampaignMutation = useMutation(
        async (data: {
            id: number
            index: number
            campaignData: PromoCampaign
        }) => await updatePromoCampaign(data.id, data.campaignData),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('freeTrials')
            },
            onError: () => {
                addToast('error', 'An error occured, please try again')
            }
        }
    )

    const deleteCampaignMutation = useMutation(
        async (id: number) => await deletePromoCampaign(id),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('freeTrials')
            },
            onError: () => {
                addToast('error', 'An error occured, please try again')
            }
        }
    )

    const { data, isLoading } = useQuery(
        'freeTrials',
        async () => await getFreeTrialLinks()
    )

    function copyToClipboard(text: string) {
        if (navigator?.clipboard && window?.isSecureContext) {
            navigator.clipboard.writeText(text);
        } else {
            // Fallback for older browsers
            const textarea = document.createElement("textarea");
            textarea.value = text;
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand("copy");
            document.body.removeChild(textarea);
        }
    }

    const copyFreeTrialLink = (link: string) => {
        copyToClipboard(link)
        addToast('success', 'Successfully copied')
    }
    return (
        <WithHeaderSection
            headerSection={
                <LayoutHeader
                    type='basic'
                    title='Free Trials'
                    section={t('subscriptions')}
                />
            }
        >
            <div className={style.filter_tabs}>
                {filterTabs.map((tab: string) => (
                    <div
                        key={tab}
                        className={`${style.filter_tab} ${
                            activeFilter === tab ? style.filter_tab_active : ''
                        }`}
                        onClick={() => {
                            setActiveFilter(tab)
                        }}
                    >
                        {tab}
                    </div>
                ))}
            </div>
            {isLoading && <div className='loader'></div>}

            {!isLoading &&
                data
                    ?.filter((trial: any) => {
                        const today = new Date()
                        if (activeFilter === 'Active') {
                            return (
                                trial.active === 1 &&
                                today.getTime() <
                                    new Date(trial.expiry_at).getTime()
                            )
                        }
                        if (activeFilter === 'Disabled')
                            return trial.active === 0
                        if (activeFilter === 'Expired')
                            return (
                                new Date(trial.expiry_at).getTime() <=
                                today.getTime()
                            )
                        return false
                    })
                    .map((trial: any) => {
                        const createdDate = new Date(trial.created_at)
                        const expireDate = new Date(trial.expiry_at)
                        const optionsOpen = !!openTrialOptions[trial.id]

                        return (
                            <SubscriptionCard
                                type='card'
                                customClass={style.free_trial_card}
                                key={trial.id}
                                icon={<IconDollarCircleArrowsNoShadow />}
                                alwaysExpanded={true}
                                selectable={false}
                                hideArrow={true}
                                isActive={trial.active === 1}
                                switchFn={() => {
                                    toggleCampaignMutation.mutate({
                                        id: trial.id,
                                        index: data.indexOf(trial),
                                        campaignData: {
                                            type: trial.type,
                                            audience: trial.audience,
                                            duration: trial.duration,
                                            limit: trial.limit,
                                            length: trial.length,
                                            active: !trial.active
                                        }
                                    })
                                }}
                                editFn={() => {
                                    modalData.addModal(
                                        'Update Free Trial',
                                        <AddTrialLink
                                            isEdit={true}
                                            campaign={trial}
                                        />
                                    )
                                }}
                                deleteFn={
                                    optionsOpen && activeFilter !== 'Expired'
                                        ? () => {
                                            deleteCampaignMutation.mutate(trial.id)
                                        }
                                        : undefined
                                }
                                hideEdit={activeFilter === 'Expired' || !optionsOpen}
                                hideToggle={activeFilter === 'Expired' || !optionsOpen}
                                name={
                                    <div className={style.trial_header}>
                                        <div className={style.trial_header_text}>
                                            <div className={style.card_title}>
                                                Trial
                                            </div>

                                            <div className={style.duration}>
                                                Duration <span>{trial.duration} Days</span>
                                            </div>
                                        </div>

                                        <button
                                            type='button'
                                            className={`${style.trial_options_button} ${
                                                optionsOpen
                                                    ? style.trial_options_button_open
                                                    : ''
                                            }`}
                                            onClick={event => {
                                                event.stopPropagation()

                                                setOpenTrialOptions(prev => ({
                                                    ...prev,
                                                    [trial.id]: !prev[trial.id]
                                                }))
                                            }}
                                        >
                                            <IconDownChevron />
                                        </button>
                                    </div>
                                }
                                descritpion={
                                    <div
                                        className={`${style.description_container} ${
                                            optionsOpen ? style.description_container_open : ''
                                        }`}
                                    >
                                        <div
                                            className={style.description_title}
                                        >
                                            Details
                                        </div>

                                        <div
                                            className={
                                                style.description_date_container
                                            }
                                        >
                                            <div
                                                className={
                                                    style.description_date
                                                }
                                            >
                                                Created{' '}
                                                <span>
                                                    {
                                                        dateArray[
                                                            createdDate.getMonth()
                                                        ]
                                                    }{' '}
                                                    {createdDate.getDate()}{' '}
                                                    {createdDate.getFullYear()}
                                                </span>
                                            </div>
                                            <div
                                                className={
                                                    style.description_date
                                                }
                                            >
                                                Expires{' '}
                                                <span>
                                                    {
                                                        dateArray[
                                                            expireDate.getMonth()
                                                        ]
                                                    }{' '}
                                                    {expireDate.getDate()}{' '}
                                                    {expireDate.getFullYear()}
                                                </span>
                                            </div>
                                        </div>

                                        <div
                                            className={style.description_limit}
                                        >
                                            <IconDotInCircle />
                                            <span>
                                                {trial.limit} Subscriptions Max
                                            </span>
                                        </div>

                                        <div
                                            className={
                                                style.description_options
                                            }
                                        >
                                        <button
                                            className={style.description_copy_link}
                                            onClick={() => {
                                                copyFreeTrialLink(trial.link)
                                            }}
                                        >
                                            Copy Link
                                        </button>

                                            <div
                                                className={
                                                    style.description_claimed_by
                                                }
                                                onClick={() =>
                                                    trial.used > 0
                                                        ? modalData.addModal(
                                                              'Trial Claimed By',
                                                              <ClaimedUsersModal
                                                                  users={
                                                                      trial.users
                                                                  }
                                                              />
                                                          )
                                                        : null
                                                }
                                            >
                                                <div
                                                    className={
                                                        style.description_claimed_by_text
                                                    }
                                                >
                                                    Claimed by
                                                </div>{' '}
                                                {trial.used > 0 ? (
                                                    <div
                                                        className={
                                                            style.stacked_img_container
                                                        }
                                                    >
                                                        <StackedAvatar
                                                            avatars={trial.users
                                                                .map((user: any) => user.avatar?.url)
                                                                .filter(Boolean)}
                                                            count={trial.used}
                                                            avtarsCount={5}
                                                            customClass={style.stacked_img}
                                                        />
                                                    </div>
                                                ) : (
                                                    <span>
                                                        {trial.used} Users
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                }
                            />
                        )
                    })}
            {!isLoading && (
                <SubscriptionCard
                    clickFn={() => {
                        modalData.addModal('Free Trials', <AddTrialLink />)
                    }}
                    name='Generate New Link'
                />
            )}
        </WithHeaderSection>
    )
}
